import React, { useEffect } from "react";
import { useParams } from "react-router-dom";
import Managed, { useManaged } from "../../common/components/Managed";
import api from "../../common/lib/api";
import StaticTable from "../../common/components/StaticTable";
import { Tabs, Tab, Intent, Callout } from "@blueprintjs/core";
import AsyncButton from "../../common/components/AsyncButton";
import moment from "moment";

const USAGE_FIELDS = [
  { id: "time", name: "Time", type: "datetime", defaultOrder: -1 },
  { id: "deviceId", name: "Device" },
  { id: "body", name: "Note" },
];

const BILLING_FIELDS = [{ id: "period", name: "Period", defaultOrder: -1 }];

export default function Page() {
  const { licenseEmail } = useParams();
  const [state, setState, setError] = useManaged();

  async function load(licenseEmail, setState, setError) {
    try {
      const { data } = await api.get("/pcs-licenses/" + licenseEmail);
      setState(data.data);
    } catch (e) {
      setError(e);
    }
  }

  async function deleteLicense(licenseEmail) {
    try {
      await api.delete("/pcs-licenses/" + licenseEmail);
    } catch (e) {
      setError(e);
      throw e;
    }
  }

  async function addCredit(credits) {
    try {
      await api.post("/pcs-licenses/" + licenseEmail + "/add-credits", {
        credits,
      });
    } catch (e) {
      setError(e);
      throw e;
    }
  }

  useEffect(() => {
    load(licenseEmail, setState, setError);
  }, [licenseEmail, setError, setState]);

  return (
    <>
      <Managed state={state}>
        <>
          <h1>PCS License - {state.data && state.data.email}</h1>

          {state.data && (
            <>
              <table>
                <tbody>
                  <tr>
                    <td>
                      <strong>Company ID:</strong>
                    </td>
                    <td>{state.data.companyId}</td>
                  </tr>
                  <tr>
                    <td>
                      <strong>Company Name:</strong>
                    </td>
                    <td>{state.data.companyName}</td>
                  </tr>
                  <tr>
                    <td>
                      <strong>Email:</strong>
                    </td>
                    <td>{state.data.email}</td>
                  </tr>
                  <tr>
                    <td>
                      <strong>First Name:</strong>
                    </td>
                    <td>{state.data.firstName}</td>
                  </tr>
                  <tr>
                    <td>
                      <strong>Last Name:</strong>
                    </td>
                    <td>{state.data.lastName}</td>
                  </tr>
                  <tr>
                    <td>
                      <strong>Mobile:</strong>
                    </td>
                    <td>{state.data.mobile}</td>
                  </tr>
                  <tr>
                    <td>
                      <strong>User Type:</strong>
                    </td>
                    <td>{state.data.userType}</td>
                  </tr>
                  {state.data.userType === "USER ERROR" && (
                    <tr>
                      <td colSpan="2">
                        <span>
                          <Callout title={"USER ERROR"} intent="danger">
                            Generally means that the user was deleted without
                            deleting the license.
                          </Callout>
                        </span>
                      </td>
                    </tr>
                  )}
                  <tr>
                    <td>
                      <strong>Status:</strong>
                    </td>
                    <td>
                      {state.data.status}{" "}
                      {state.data.status === "active" && (
                        <AsyncButton
                          intent={Intent.DANGER}
                          onClick={async () => {
                            if (
                              // eslint-disable-next-line no-restricted-globals
                              confirm(
                                `Delete PCS license for ${state.data.email}?`
                              )
                            ) {
                              await deleteLicense(licenseEmail);
                              alert("License deleted!");
                              window.location.href = "/manage/pcs-licenses";
                            }
                          }}
                        >
                          Delete License
                        </AsyncButton>
                      )}
                    </td>
                  </tr>
                  <tr>
                    <td>
                      <strong>Credits:</strong>
                    </td>
                    <td>
                      {state.data.uploadDownloadCredits}{" "}
                      {state.data.status === "active" && (
                        <AsyncButton
                          intent={Intent.WARNING}
                          onClick={async () => {
                            // eslint-disable-next-line no-restricted-globals
                            const credits = parseInt(
                              prompt(
                                "How many credits would you like to add?",
                                10
                              )
                            );
                            if (credits && !Number.isNaN(credits)) {
                              if (
                                // eslint-disable-next-line no-restricted-globals
                                confirm(`Add ${credits} credits to the user?`)
                              ) {
                                await addCredit(credits);
                                alert("Credits added");
                                window.location.reload();
                              }
                            }
                          }}
                        >
                          Give Credit
                        </AsyncButton>
                      )}
                    </td>
                  </tr>

                  <tr>
                    <td>
                      <strong>Expires:</strong>
                    </td>
                    <td>{formatDate(state.data.expireAt)}</td>
                  </tr>
                  <tr>
                    <td>
                      <strong>Last Use:</strong>
                    </td>
                    <td>{formatDate(state.data.lastUsedAt)}</td>
                  </tr>
                  <tr>
                    <td>
                      <strong>Last Renewed:</strong>
                    </td>
                    <td>{formatDate(state.data.period, "MMMM YYYY")}</td>
                  </tr>
                </tbody>
              </table>

              <Tabs>
                <Tab
                  id="usage"
                  title={"Usage"}
                  panel={
                    <StaticTable
                      fields={USAGE_FIELDS}
                      items={state.data.usageHistory || []}
                    />
                  }
                />
                <Tab
                  id="billing"
                  title={"Billing"}
                  panel={
                    <StaticTable
                      fields={BILLING_FIELDS}
                      items={state.data.billingHistory || []}
                    />
                  }
                />
              </Tabs>
            </>
          )}
        </>
      </Managed>
    </>
  );
}

function formatDate(date, format = "llll") {
  if (date) {
    let text = moment(date).format(format);
    let fromNow = moment(date).fromNow();
    if (text !== fromNow) {
      return `${text} (${fromNow})`;
    } else {
      return text;
    }
  }

  return "";
}
